import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../utils/statsFormat';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Person {
  id: string;
  email: string;
  name: string | null;
  role: string;
  active: boolean;
  last_login: string | null;
  logins: number;
  posts_created: number;
  posts_published: number;
  posts_all_time: number;
}

interface Key {
  id: string;
  name: string;
  prefix: string;
  owner: string | null;
  created_at: string;
  last_used_at: string | null;
  requests: number;
  revoked: boolean;
  posts_created: number;
  posts_all_time: number;
}

interface PeopleResponse {
  days: number;
  login_history_since: string | null;
  unattributed_api_posts: number;
  people: Person[];
  keys: Key[];
}

const RANGES = [7, 30, 90, 365];

/**
 * Who uses ShoutVector and what each person and API key has put into it (2026-10-17).
 *
 * The other half of what was asked for when the Activity Log was first requested: the timeline
 * says what happened, this says who is doing it. Sign-ins are counted from the day the history
 * started, which the page says, so an early zero is not read as someone never signing in.
 */
export const ActivityPeople: React.FC = () => {
  const { token } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<PeopleResponse | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/activity/people?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(String(res.status));
      setData(await res.json());
    } catch {
      setError('Could not load people and keys.');
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Period" value={days} onChange={e => setDays(Number(e.target.value))}>
          {RANGES.map(d => <MenuItem key={d} value={d}>Last {d} days</MenuItem>)}
        </TextField>
        {data.login_history_since && (
          <Typography variant="body2" color="text.secondary">
            Sign-ins counted since {formatDate(data.login_history_since)}; before that only the most recent was kept.
          </Typography>
        )}
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>People</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  <TableCell>Last Sign-In</TableCell>
                  <TableCell align="right">Sign-Ins</TableCell>
                  <TableCell align="right">Posts Created</TableCell>
                  <TableCell align="right">Published</TableCell>
                  <TableCell align="right">All Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.people.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{p.name || p.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.name ? `${p.email} · ` : ''}{p.role}
                      </Typography>
                      {!p.active && <Chip size="small" label="Inactive" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>{formatDate(p.last_login)}</TableCell>
                    <TableCell align="right">{p.logins}</TableCell>
                    <TableCell align="right">{p.posts_created}</TableCell>
                    <TableCell align="right">{p.posts_published}</TableCell>
                    <TableCell align="right">{p.posts_all_time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>API Keys</Typography>
          {data.keys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No API keys in this workspace.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell align="right">Requests</TableCell>
                    <TableCell align="right">Posts Created</TableCell>
                    <TableCell align="right">All Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.keys.map(k => (
                    <TableRow key={k.id} sx={k.revoked ? { opacity: 0.6 } : undefined}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{k.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {k.prefix}… · {k.owner || 'unknown owner'}
                        </Typography>
                        {k.revoked && <Chip size="small" label="Revoked" sx={{ ml: 1 }} />}
                      </TableCell>
                      <TableCell>{formatDate(k.last_used_at)}</TableCell>
                      <TableCell align="right">{k.requests.toLocaleString()}</TableCell>
                      <TableCell align="right">{k.posts_created}</TableCell>
                      <TableCell align="right">{k.posts_all_time}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {data.unattributed_api_posts > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {data.unattributed_api_posts} earlier API {data.unattributed_api_posts === 1 ? 'post was' : 'posts were'} made
              before keys were recorded against posts, so {data.unattributed_api_posts === 1 ? 'it is' : 'they are'} not counted per key.
              Requests are all-time.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
